import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ListItem,
  Avatar,
  useTheme,
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { AlertTriangle, X, ArchiveRestore } from 'lucide-mui';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';
import { RouteParams } from '../types';

const RecoveryFileListItem: React.FC = () => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const { folderId } = useParams<RouteParams>();
  const isInRecentsView = folderId === 'recents';
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    hasRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = () => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  };

  const handleDismissClick = (e: React.MouseEvent, fileId: string) => {
    e.stopPropagation();
    handleDismiss(fileId);
  };

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleRecoverClick}
      sx={{
        borderBottom: theme.palette.mode === 'dark' ? 0 : 1,
        borderColor: theme.palette.secondary.light,
        position: 'relative',
        py: 1.5,
        pr: 2,
        backgroundColor: isHovered ? theme.palette.action.hover : 'inherit',
        transition: 'background-color 0.3s',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(200px, 1fr) 65px 70px',
            sm: isInRecentsView
              ? 'minmax(200px, 1.5fr) 80px 100px 80px 24px'
              : 'minmax(200px, 1.5fr) 80px 80px 24px',
            md: isInRecentsView
              ? 'minmax(200px, 1.5fr) 80px 100px 120px 80px 24px'
              : 'minmax(200px, 1.5fr) 80px 120px 80px 24px',
            lg: isInRecentsView
              ? 'minmax(250px, 1.5fr) 100px 120px 120px 80px 24px'
              : 'minmax(250px, 1.5fr) 100px 150px 80px 24px',
          },
          alignItems: 'center',
          width: '100%',
          gap: { xs: 1, sm: 2 },
          pl: 2,
        }}
      >
        {/* Name Column */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              sx={{
                width: { xs: 24, sm: 32 },
                height: { xs: 24, sm: 32 },
                backgroundColor: theme.palette.secondary.main,
                color: 'white',
                borderRadius: 1,
                flexShrink: 0,
              }}
            >
              {isHovered ? (
                <ArchiveRestore sx={{ width: 16, height: 16 }} />
              ) : (
                <AlertTriangle sx={{ width: 16, height: 16 }} />
              )}
            </Avatar>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                fontWeight: 600,
              }}
            >
              {hasMultiple
                ? `${recentRecoverableFiles.length} uploads to finalize`
                : 'Finalize upload'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                color: 'text.secondary',
              }}
            >
              {isHovered ? 'Click to finalize' : 'Upload was interrupted'}
            </Typography>
          </Box>
        </Box>

        {/* Duration Column - Show progress or status */}
        {isRecovering ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} color="secondary" />
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}%
            </Typography>
          </Box>
        ) : (
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            —
          </Typography>
        )}

        {/* Folder Column - Only show in recents view */}
        {isInRecentsView && (
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: 'text.secondary',
              opacity: 0.7,
            }}
          >
            —
          </Typography>
        )}

        {/* Creator Column - Show dash */}
        <Typography
          sx={{
            display: { xs: 'none', md: 'block' },
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          —
        </Typography>

        {/* Date Column - Show time ago */}
        <Typography
          sx={{
            display: 'block',
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          {firstRecord && getTimeAgo(firstRecord.startedAt)}
        </Typography>
      </Box>

      {/* Dismiss Button */}
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
        }}
      >
        <Tooltip title="Dismiss">
          <IconButton
            size="small"
            onClick={(e) => {
              if (firstRecord) {
                handleDismissClick(e, firstRecord.fileId);
              }
            }}
            sx={{
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(42, 43, 44, 0.9)'
                  : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(4px)',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(42, 43, 44, 1)'
                    : 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <X sx={{ width: 14, height: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </ListItem>
  );
};

export default RecoveryFileListItem;
